import { ProjectService, VideoService } from '@/services';
import { UploadSource } from '@/types/common';
import { writable } from 'svelte/store';
const initialState = {
    video: null,
    template: null,
    compiledVideo: null,
    usesAICameraCrew: false,
    usesAICameraCrewV2: false,
    selectedSentences: [],
    totalDuration: 0,
    changes: [],
    isUpdated: false,
    isNew: true,
    uploadProgress: 0,
    uploadLoaded: 0,
    uploadTotal: 0
};
// Init state
const { subscribe, update, set } = writable(structuredClone(initialState));
let projectState;
subscribe(project => (projectState = project));
export const getTotalDuration = (sentences) => {
    let _total = 0;
    let lastIndex = null;
    let cutData = [];
    sentences.forEach(sentence => {
        const start = sentence.words[0].timeStamp?.startTimeRaw;
        const end = sentence.words[sentence.words.length - 1].timeStamp?.endTimeRaw;
        const segment = { start, end };
        if (lastIndex !== null && lastIndex == sentence.index - 1) {
            const connectedSegment = cutData[cutData.length - 1];
            connectedSegment.end = segment.end;
        }
        else {
            cutData.push(segment);
        }
        lastIndex = sentence.index;
    });
    cutData.forEach(segment => {
        let _computedDuration = (segment.end - segment.start) / 1000;
        _total = _total + _computedDuration;
    });
    return _total;
};
let videoService = new VideoService();
let projectService = new ProjectService();
const actions = {
    reset: () => {
        update(store => structuredClone(initialState));
    },
    set: (updatedState) => {
        update(store => ({ ...store, ...updatedState }));
    },
    updateTemplate: (template) => {
        update(store => {
            let temp = store.template;
            if (temp) {
                temp.content = template;
                store.template = temp;
            }
            return { ...store };
        });
    },
    createDownloadable: async (projectId, url) => {
        const data = await projectService.createDownloadable(projectId, url);
        return data;
    },
    updated() {
        update(store => {
            return { ...store, isUpdated: true };
        });
    },
    insertChange: (change) => {
        update(store => {
            let isUpdated = true;
            if (change.word) {
                let currentChange = store.changes.find(item => item.word?.index === change.word?.index);
                if (!currentChange) {
                    store.changes.push(change);
                }
                else {
                    currentChange = change;
                }
            }
            else if (change.sentence) {
                store.changes.push(change);
            }
            return { ...store, isUpdated, compiledVideo: null, usesAICameraCrew: false, compiledAICameraCrewVideo: null };
        });
    },
    updateProgress(updatedProgress) {
        update(store => ({ ...store, ...updatedProgress }));
    },
    async uploadVideo(video, callback) {
        const __uploadedVideo = await new VideoService().uploadVideo(video, progressEvent => {
            const { loaded, total } = progressEvent;
            const progress = (loaded / total) * 100;
            update(store => {
                return {
                    ...store,
                    uploadProgress: progress,
                    uploadLoaded: loaded,
                    uploadTotal: total
                };
            });
            callback(progress);
        });
        update(store => {
            return {
                ...store,
                video: __uploadedVideo
            };
        });
    },
    async uploadYoutubeVideo({ url, language, format, source = UploadSource.YOUTUBE, videoId, options }) {
        // unselect any video first
        const __uploadedVideo = await new VideoService().uploadYoutubeVideo({
            url,
            language,
            format,
            source,
            videoId,
            options
        });
        if (__uploadedVideo) {
            update(store => {
                return {
                    ...store,
                    video: __uploadedVideo
                };
            });
        }
    },
    async getVideoUploadStatus() {
        return await new VideoService().getVideoUploadStatus(projectState.video?.id);
    },
    async getVideo(id) {
        try {
            const videoID = !!id ? id : projectState.video?.id;
            const video = await videoService.getVideo(videoID);
            update(store => {
                return {
                    ...store,
                    video
                };
            });
            return { video, error: null };
        }
        catch (error) {
            return { video: null, error: 'failed to get video' };
        }
    },
    async selectVideo(selectedVideo) {
        update(store => ({
            ...store,
            video: null
        }));
        // Request for video to load transcript as the transcript data will not be loaded on the Choose Video page
        const video = await videoService.getVideo(selectedVideo.id);
        update(store => {
            return {
                ...store,
                video: video
            };
        });
        return video;
    },
    resetState() {
        update(() => {
            return { ...initialState };
        });
    },
    async setVideo(fullVideo) {
        update(store => {
            return {
                ...store,
                video: fullVideo
            };
        });
        return fullVideo;
    },
    setOriginalTemplateHTML(templateHTML) {
        update(store => ({
            ...store,
            originalTemplateHTML: templateHTML
        }));
    },
    selectTemplate(selectedTemplate) {
        console.warn('Selecting template', selectedTemplate);
        update(store => {
            return {
                ...store,
                template: selectedTemplate,
                originalTemplateHTML: selectedTemplate ? selectedTemplate.content : null
            };
        });
    },
    selectSentence(sentence) {
        update(store => {
            let isUpdated = true;
            let sentenceArray = [...store.selectedSentences, sentence].sort((a, b) => {
                return a.index - b.index;
            });
            let selectedSentences = [...new Set(sentenceArray)];
            let totalDuration = getTotalDuration(selectedSentences);
            return {
                ...store,
                isUpdated,
                compiledVideo: null,
                usesAICameraCrew: false,
                usesAICameraCrewV2: false,
                compiledAICameraCrewVideoV2: null,
                compiledAICameraCrewVideo: null,
                totalDuration,
                selectedSentences
            };
        });
    },
    unselectSentence(sentence) {
        update(store => {
            let isUpdated = true;
            let selectedSentences = store.selectedSentences.filter(sentenceInArray => sentence.index != sentenceInArray.index);
            let totalDuration = getTotalDuration(selectedSentences);
            return {
                ...store,
                isUpdated,
                compiledVideo: null,
                usesAICameraCrew: false,
                usesAICameraCrewV2: false,
                compiledAICameraCrewVideoV2: null,
                compiledAICameraCrewVideo: null,
                totalDuration,
                selectedSentences
            };
        });
    },
    updateSentence(sentence) {
        if (!sentence)
            return;
        update(store => {
            this.insertChange({ sentence });
            if (!store.selectedSentences || store.selectedSentences.length == 0)
                return store;
            let selectedSentence = store.selectedSentences.find(selected => {
                return selected.index == sentence.index;
            });
            if (selectedSentence) {
                store.selectedSentences[store.selectedSentences.indexOf(selectedSentence)] = sentence;
            }
            let totalDuration = getTotalDuration(store.selectedSentences);
            return { ...store, totalDuration };
        });
    },
    async updateTranscription() {
        if (!projectState.video)
            return false;
        await new ProjectService().updateTranscription(projectState.video.id, projectState.changes);
    },
    async clearChanges() {
        update(store => {
            store.changes = [];
            return store;
        });
    }
};
const project = {
    subscribe,
    update,
    ...actions
};
export default project;
